<template>
  <div class="attendance-buttons">
    <span class="label">
      <MaybeIcon class="label-icon" />
      Attend
    </span>
    <div class="buttons-group">
      <button
        v-for="action in actions"
        :key="action.text"
        class="attendance-button"
        :class="[
          getAttendanceType(action.text),
          { selected: isSelected(action.text) },
        ]"
        @click="$emit('select', action)"
      >
        <span class="button-text">{{ getButtonLabel(action.text) }}</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { capitalize, defineComponent, PropType } from "vue";

import type { YesNoMaybe } from "@ctrl/controller-rpc";

import { RunnableContextAction } from "@/components/context-menu/context-actions";
import MaybeIcon from "@/components/icons/MaybeIcon.vue";

export default defineComponent({
  components: { MaybeIcon },
  props: {
    actions: {
      type: Array as PropType<RunnableContextAction[]>,
      required: true,
    },
    currentAttendance: {
      type: String as PropType<YesNoMaybe | null>,
      default: null,
    },
  },
  emits: ["select"],
  setup(props) {
    const getButtonLabel = (text: string): string => {
      // Extract "yes", "no", "maybe" from "Attend: yes", "Attend: no", "Attend: maybe"
      const match = text.match(/Attend:\s*(.+)/);
      const label = match ? match[1] : text;
      return capitalize(label);
    };

    const getAttendanceType = (text: string): string => {
      if (text.includes("yes")) return "going";
      if (text.includes("no")) return "decline";
      if (text.includes("maybe")) return "question";
      return "";
    };

    const isSelected = (text: string): boolean => {
      const label = getButtonLabel(text).toLowerCase();
      return label === props.currentAttendance;
    };

    return {
      getButtonLabel,
      getAttendanceType,
      isSelected,
    };
  },
});
</script>

<style scoped lang="scss">
.attendance-buttons {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;

  .label {
    display: flex;
    align-items: center;
    gap: 6px;
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-70);
    }

    .label-icon {
      height: 12px;
      width: 12px;

      :deep(svg) {
        height: 12px;
        width: 12px;
      }
    }
  }

  .buttons-group {
    display: flex;
    flex-direction: row;
    gap: 4px;
    flex: 1;
  }

  .attendance-button {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: $border-radius-m;
    border: 1px solid;
    @extend %font-caption-regular;
    @include transition(all 100ms ease);
    transition-property: color, background-color, border;
    flex: 1;
    justify-content: center;

    @include themed() {
      background: t($bg);
      color: t($grey-70);
    }

    &:hover {
      @include themed() {
        background: t($bg-menu-hover);
        color: t($grey-80);
      }
    }

    &:active {
      @include themed() {
        background: t($grey-15);
      }
    }

    .button-text {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    // Selected states with specific colors
    &.going {
      &.selected {
        color: $accepted;
        @include themed() {
          background-color: t($bg);
        }
      }
    }

    &.question {
      &.selected {
        color: $maybe;
        @include themed() {
          background-color: t($bg);
        }
      }
    }

    &.decline {
      &.selected {
        color: $declined;
        @include themed() {
          background-color: t($bg);
        }
      }
    }
  }
}
</style>
